import React, {useEffect, useState} from 'react';
import {orgId} from '../../../context/context';
import {TextField} from '../../../libs/react-components-admin/fields/text-like-field';
import SubSection from '../../../libs/react-components-admin/section/sub-section';
import {FieldListMapping, FieldMapping} from '../../../libs/react-components-admin/integration/mappings';
import SelectField from '../../../libs/react-components-admin/fields/select-field';
import CenteredSpinner from '../../../libs/react-components-admin/spinners/centered-spinner';

// The commented types don't work, see https://community.shopify.com/c/shopify-apis-and-sdks/cannot-use-graphql-api-metafieldset-for-onlinestorepage-and/td-p/1535806
const ownerTypes = [
  // {label: 'Blog', value: 'blog', requiredAccessScope: 'write_content'},
  // {label: 'Blog post', value: 'blog-post', requiredAccessScope: 'write_content'},
  {label: 'Collection', value: 'collection', requiredAccessScope: 'write_products'},
  {label: 'Customer', value: 'customer', requiredAccessScope: 'write_customers'},
  {label: 'Order', value: 'order', requiredAccessScope: 'write_orders'},
  // {label: 'Page', value: 'page', requiredAccessScope: 'write_content'},
  {label: 'Product', value: 'product', requiredAccessScope: 'write_products'},
  {label: 'Product variant', value: 'product-variant', requiredAccessScope: 'write_products'},
];

const Owner = ({grantedAccessScopes, sourceFields, initialModel, onChange}) => {

  const [model, setModel] = useState(initialModel || {type: ownerTypes[0].value});
  const [_sourceFields, setSourceFields] = useState(sourceFields);

  useEffect(() => { onChange(model); }, [model]);
  useEffect(() => { setSourceFields(sourceFields); }, [sourceFields]);

  const ownerTypesWithDisabled = () => {
    return ownerTypes.map(ownerType => ({
      ...ownerType,
      disabled: !grantedAccessScopes.includes(ownerType.requiredAccessScope),
    }));
  };

  return <SubSection title="Owner">
    <div className="text-muted mb-2">
      The owner specifies the entity for which the metafield will be saved (e.g. a customer, an order, etc).<br/>
      Some types in the list might be disabled if you haven&apos;t granted the required permission on the <a
      href={'/integrations/shopify?shop=' + orgId}>Shopify integrations page</a>.
    </div>
    <SelectField id="owner-type"
                 label="Type"
                 values={ownerTypesWithDisabled()}
                 value={model.type}
                 onChange={v => setModel({...model, type: v})}/>
    <FieldMapping label="ID"
                  sourceFields={_sourceFields}
                  initialModel={model.idMapping}
                  onChange={v => setModel({...model, idMapping: v})}/>
    <div className="form-text">When the form is submitted, the field that contains the ID of the owner.</div>
  </SubSection>;
};

const metafieldTypes = [
  {label: 'Single line text', value: 'single_line_text_field'},
  {label: 'Single line text (List)', value: 'list.single_line_text_field'},
  {label: 'Multi-line text', value: 'multi_line_text_field'},
  {label: 'Integer', value: 'number_integer'},
  {label: 'Decimal', value: 'number_decimal'},
  // Date requires an ISO format, it would have to be sent from custom-forms to shopify-service
  // {label: 'Date', value: 'date'},
  {label: 'True or false', value: 'boolean'},
  // Color requires an hex format, it would have to be sent from custom-forms to shopify-service
  // {label: 'Color', value: 'color'},
  {label: 'URL', value: 'url'},
];

const sourceFieldsForType = (sourceFields, type) => {
  switch (type) {
  case 'number_integer':
  case 'number_decimal':
    return sourceFields.filter(f => f.fieldType === 'NUMBER');
  case 'date':
    return sourceFields.filter(f => f.fieldType === 'DATE');
  case 'boolean':
    return sourceFields.filter(f => f.fieldType === 'CHECKBOX');
  case 'color':
    return sourceFields.filter(f => f.fieldType === 'COLOR');
  case 'url':
    return sourceFields.filter(f => f.fieldType === 'FILE');
  default:
    return sourceFields;
  }
};

const MetafieldMapping = ({isSubIntegration, grantedAccessScopes, sourceFields, initialModel, onChange}) => {

  const [model, setModel] = useState(initialModel && initialModel.type ?
    initialModel :
    {type: metafieldTypes[0].value}
  );
  const [_ownerSourceFields, setOwnerSourceFields] = useState();
  const [_valueSourceFields, setValueSourceFields] = useState();

  useEffect(() => { onChange(model); }, [model]);
  useEffect(() => {
    setOwnerSourceFields(sourceFields.filter(f => ['TEXT_FIELD', 'HIDDEN'].includes(f.fieldType)));
  }, [sourceFields]);
  useEffect(
    () => { setValueSourceFields(sourceFieldsForType(sourceFields, model.type)); },
    [sourceFields, model.type]
  );

  if (!_ownerSourceFields || !_valueSourceFields) {
    return <CenteredSpinner/>;
  }
  return <>
    <div className="row">
      <div className="col-12">
        <TextField label="Name"
                   helpBlock={<>Helps you identify this metafield mapping</>}
                   value={model.name}
                   onChange={v => setModel({...model, name: v})}/>
      </div>
      <div className="col-12 col-md-6">
        <TextField label="Namespace"
                   value={model.namespace}
                   onChange={v => setModel({...model, namespace: v})}/>
      </div>
      <div className="col-12 col-md-6">
        <TextField label="Key"
                   value={model.key}
                   onChange={v => setModel({...model, key: v})}/>
      </div>
      <div className="col-12">
        <SelectField id="metafield-type"
                     label="Content type"
                     values={metafieldTypes}
                     value={model.type}
                     onChange={v => setModel({...model, type: v})}/>
      </div>
      <div className="col-12">
        {!isSubIntegration &&
          <Owner grantedAccessScopes={grantedAccessScopes}
                 sourceFields={_ownerSourceFields}
                 initialModel={model.owner}
                 onChange={v => setModel({...model, owner: v})}/>
        }
      </div>
      <div className="col-12">
        <SubSection title="Value">
          {model.type === 'list.single_line_text_field' ?
            <FieldListMapping label="Fields"
                              sourceFields={_valueSourceFields}
                              initialModel={model.valueMapping}
                              onChange={v => setModel({...model, valueMapping: v})}/> :
            <FieldMapping label="Field"
                          sourceFields={_valueSourceFields}
                          initialModel={model.valueMapping}
                          onChange={v => setModel({...model, valueMapping: v})}/>
          }
        </SubSection>
      </div>
    </div>
  </>;

};

export default MetafieldMapping;
